import '../assets/NcButton-B62D9Gft.css';
import { Comment, Fragment, Text, defineComponent, h, resolveComponent } from "vue";
import { _ as _export_sfc } from "./_plugin-vue_export-helper-1tPrXgE0.mjs";
var ButtonAlignment = /* @__PURE__ */ ((ButtonAlignment2) => {
  ButtonAlignment2["Start"] = "start";
  ButtonAlignment2["StartReverse"] = "start-reverse";
  ButtonAlignment2["Center"] = "center";
  ButtonAlignment2["CenterReverse"] = "center-reverse";
  ButtonAlignment2["End"] = "end";
  ButtonAlignment2["EndReverse"] = "end-reverse";
  return ButtonAlignment2;
})(ButtonAlignment || {});
var ButtonNativeType = /* @__PURE__ */ ((ButtonNativeType2) => {
  ButtonNativeType2["Submit"] = "submit";
  ButtonNativeType2["Reset"] = "reset";
  ButtonNativeType2["Button"] = "button";
  return ButtonNativeType2;
})(ButtonNativeType || {});
var ButtonType = /* @__PURE__ */ ((ButtonType2) => {
  ButtonType2["Primary"] = "primary";
  ButtonType2["Secondary"] = "secondary";
  ButtonType2["Tertiary"] = "tertiary";
  ButtonType2["TertiaryNoBackground"] = "tertiary-no-background";
  ButtonType2["TertiaryOnPrimary"] = "tertiary-on-primary";
  ButtonType2["Error"] = "error";
  ButtonType2["Warning"] = "warning";
  ButtonType2["Success"] = "success";
  return ButtonType2;
})(ButtonType || {});
const isSlotPopulated = function(vnodes) {
  return Array.isArray(vnodes) && vnodes.some((node) => {
    if (node === null) return false;
    if (typeof node === "object") {
      const vnode = node;
      if (vnode.type === Comment) return false;
      if (vnode.type === Fragment && !isSlotPopulated(vnode.children)) return false;
      if (vnode.type === Text && !vnode.children.trim()) return false;
    }
    return true;
  });
};
const _sfc_main = defineComponent({
  name: "NcButton",
  inject: {
    getNcPopoverTriggerAttrs: {
      from: "NcPopover:trigger:attrs",
      default: () => () => ({})
    }
  },
  props: {
    /**
     * Set the text and icon alignment
     *
     * @default 'center'
     * @type {'start' | 'start-reverse' | 'center' | 'center-reverse' | 'end' | 'end-reverse'}
     */
    alignment: {
      type: String,
      default: ButtonAlignment.Center,
      validator(value) {
        return Object.values(ButtonAlignment).includes(value);
      }
    },
    /**
     * Toggles the disabled state of the button on and off.
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * Specify the button size
     * Accepted values: `'small'`, `'normal'` (default), `'large'`
     */
    size: {
      type: String,
      default: "normal",
      validator(value) {
        return ["small", "normal", "large"].includes(value);
      }
    },
    /**
     * Specifies the button type
     * If left empty, the default button style will be applied.
     *
     * @default 'secondary'
     * @type {'primary' | 'secondary' | 'tertiary' | 'tertiary-no-background' | 'tertiary-on-primary' | 'error' | 'warning' | 'success'}
     */
    type: {
      type: String,
      default: ButtonType.Secondary,
      validator(value) {
        return Object.values(ButtonType).includes(value);
      }
    },
    /**
     * Specifies the button native type
     * If left empty, the default "button" type will be used.
     *
     * @default 'button'
     * @type {'submit' | 'reset' | 'button'}
     */
    nativeType: {
      type: String,
      default: ButtonNativeType.Button,
      validator(value) {
        return Object.values(ButtonNativeType).includes(value);
      }
    },
    /**
     * Specifies whether the button should span all the available width.
     * By default, buttons span the whole width of the container.
     */
    wide: {
      type: Boolean,
      default: false
    },
    /**
     * Always try to provide an aria-label to your button. Make it more
     * specific than the button's name by provide some more context. E.g. if
     * the name of the button is "send" in the Mail app, the aria label could
     * be "Send email".
     */
    ariaLabel: {
      type: String,
      default: null
    },
    /**
     * Providing the href attribute turns the button component into an `a`
     * element.
     */
    href: {
      type: String,
      default: null
    },
    /**
     * Providing the download attribute with href downloads file when clicking.
     */
    download: {
      type: String,
      default: null
    },
    /**
     * Providing the to attribute turns the button component into a `router-link`
     * element. Takes precedence over the href attribute.
     */
    to: {
      type: [String, Object],
      default: null
    },
    /**
     * @deprecated To be removed in @nextcloud/vue 9. Migration guide: remove ariaHidden prop from NcAction* components.
     * @todo Add a check in @nextcloud/vue 9 that this prop is not provided,
     * otherwise root element will inherit incorrect aria-hidden.
     */
    ariaHidden: {
      type: Boolean,
      default: null
    },
    /**
     * The pressed state of the button if it has a checked state
     * This will add the `aria-pressed` attribute and for the button to have the primary style in checked state.
     *
     * Pressed state is not supported for links
     */
    pressed: {
      type: Boolean,
      default: null
    }
  },
  emits: ["update:pressed", "click"],
  computed: {
    /**
     * The real type to be used for the button, enforces `primary` for pressed state and, if stateful button, any other type for not pressed state
     * Otherwise the type property is used.
     */
    realType() {
      if (this.pressed) {
        return "primary";
      }
      if (this.pressed === false && this.type === "primary") {
        return "secondary";
      }
      return this.type;
    },
    /**
     * The flexbox alignment of the button content
     */
    flexAlignment() {
      return this.alignment.split("-")[0];
    },
    /**
     * If the button content should be reversed (icon on the end)
     */
    isReverseAligned() {
      return this.alignment.includes("-");
    },
    ncPopoverTriggerAttrs() {
      return this.getNcPopoverTriggerAttrs();
    }
  },
  /**
   * The render function to display the component
   *
   * @return {object|undefined} The created VNode
   */
  render() {
    const hasText = isSlotPopulated(this.$slots.default?.());
    const hasIcon = isSlotPopulated(this.$slots.icon?.());
    if (!hasText && !this.ariaLabel) {
      console.warn(
        "You need to fill either the text or the ariaLabel props in the button component.",
        {
          text: this.$slots.default?.()?.[0]?.children,
          ariaLabel: this.ariaLabel
        },
        this
      );
    }
    const isLink = this.to || this.href;
    const hasPressed = !isLink && typeof this.pressed === "boolean";
    const renderButton = ({ href, navigate, isActive } = {}) => h(
      isLink ? "a" : "button",
      {
        class: [
          "button-vue",
          `button-vue--size-${this.size}`,
          {
            "button-vue--icon-only": hasIcon && !hasText,
            "button-vue--text-only": hasText && !hasIcon,
            "button-vue--icon-and-text": hasIcon && hasText,
            [`button-vue--vue-${this.realType}`]: this.realType,
            "button-vue--wide": this.wide,
            [`button-vue--${this.flexAlignment}`]: this.flexAlignment !== "center",
            "button-vue--reverse": this.isReverseAligned,
            active: isActive
          }
        ],
        "aria-label": this.ariaLabel,
        "aria-pressed": hasPressed ? this.pressed.toString() : void 0,
        disabled: this.disabled,
        type: isLink ? null : this.nativeType,
        role: isLink ? "button" : null,
        href: this.to ? href : this.href || null,
        target: isLink ? "_self" : null,
        rel: isLink ? "nofollow noreferrer noopener" : null,
        download: !this.to && this.href && this.download ? this.download : null,
        // If this button is used as a popover trigger, we need to apply trigger attrs, e.g. aria attributes
        ...this.ncPopoverTriggerAttrs,
        onClick: ($event) => {
          if (hasPressed) {
            this.$emit("update:pressed", !this.pressed);
          }
          this.$emit("click", $event);
          navigate?.($event);
        }
      },
      [
        h("span", { class: "button-vue__wrapper" }, [
          hasIcon ? h(
            "span",
            {
              class: "button-vue__icon",
              attrs: {
                "aria-hidden": "true"
              }
            },
            [this.$slots.icon?.()]
          ) : null,
          hasText ? h("span", { class: "button-vue__text" }, [this.$slots.default?.()]) : null
        ])
      ]
    );
    if (this.to) {
      return h(resolveComponent("router-link"), {
        custom: true,
        to: this.to
      }, {
        default: renderButton
      });
    }
    return renderButton();
  }
});
const NcButton = /* @__PURE__ */ _export_sfc(_sfc_main, [["__scopeId", "data-v-160d481d"]]);
export {
  ButtonAlignment as B,
  NcButton as N,
  ButtonNativeType as a,
  ButtonType as b,
  isSlotPopulated as i
};
